import { BaseQueryFn } from '@reduxjs/toolkit/dist/query';

import { ApiError } from '@/api/types/ApiError';

import { ApiMeta } from '../types/ApiMeta';
import { UnknownApiError } from '../types/UnknownApiError';
import { axiosRequest, RequestConfig } from './axiosRequest';

/**
 * Extra options to be passed on to the base query.
 */
type ExtraOptions = Partial<{
  abort: boolean;
}>;

/**
 * Type for the return value from RTK Query's baseQuery.
 * The base query in a query function is not typed properly, thus
 * we have this type that corresponds with what the RTK base query returns.
 *
 * In the createApi() call, the fetchWithBQ argument is not typed, thus provide
 * the type here.
 *
 * @example
 *  async queryFn(
 *      arg,
 *      _queryApi,
 *      _extraOptions,
 *      fetchWithBQ: (args: RequestConfig) => Promise<RtkBaseQueryResponse>,
 *      // Add the types to the fetchWithBQ argument -------^
 *    ) {
 * ...
 * }
 */
export type RtkBaseQueryResponse<TData = unknown> =
  // Successful response.
  | {
      data: TData;
      meta: ApiMeta;
      error?: never;
    }
  // Error response.
  | {
      data?: never;
      meta?: never;
      error: ApiError;
    };

export function axiosBaseQuery<ResponseData = unknown>(): BaseQueryFn<
  RequestConfig,
  ResponseData,
  ApiError | UnknownApiError,
  ExtraOptions,
  ApiMeta
> {
  return async (requestConfig, api, extraOptions) => {
    if (extraOptions?.abort) {
      const controller = new AbortController();
      requestConfig.signal = controller.signal;
      controller.abort();
    } else {
      requestConfig.signal = api.signal;
    }

    const query = await axiosRequest<ResponseData>(requestConfig);

    if (query.error) {
      return {
        error: query.error,
      };
    }

    return {
      data: query.response?.data,
      meta: {
        statusCode: query.response?.status,
      },
    };
  };
}
